/**
 * Permission utility functions for role-based access control
 *
 * Roles: Owner > Admin > Staff
 * Admin has Owner-level CRUD/management except Revenue Simulation, Reports,
 * and privilege escalation (enforced in nav + CollaboratorsPage).
 *
 * First file migrated to TypeScript (Sprint 6.2). Types are intentionally light;
 * navigation/plans are still .js (imported permissively).
 */
import { getNavigationItemByPath, PERMISSIONS, ROLES } from "../config/navigation";
import { canAccessFeature } from "./plans";

type Role = string;
type Resource = string;

interface AuthUser {
  role?: string;
  plan?: string;
}

interface NavItem {
  requiredRole?: string;
  requiredPermission?: string;
  requiredPlan?: string;
}

interface NavSection {
  requiredRole?: string;
}

// ── Resource-specific permission maps ──────────────────────────────

/**
 * Resources where Staff can CREATE (others are owner-only).
 * Staff can create: guests, reservations, activity_reservations.
 * Staff CANNOT create: room_types, rooms, activities, staff, collaborators.
 */
const STAFF_CAN_CREATE = new Set<Resource>(["guests", "reservations", "activity_reservations"]);

/**
 * Resources where Staff can EDIT (all operational resources).
 */
const STAFF_CAN_EDIT = new Set<Resource>([
  "guests",
  "reservations",
  "activity_reservations",
]);

// ── Core permission helpers (resource-specific) ────────────────────

/** Check if a role can create a given resource */
export const canCreate = (role: Role, resource: Resource): boolean => {
  if (role === ROLES.OWNER || role === ROLES.ADMIN) return true;
  if (role === ROLES.STAFF) return STAFF_CAN_CREATE.has(resource);
  return false; // unknown role
};

/** Check if a role can edit a given resource */
export const canEdit = (role: Role, resource: Resource): boolean => {
  if (role === ROLES.OWNER || role === ROLES.ADMIN) return true;
  if (role === ROLES.STAFF) return STAFF_CAN_EDIT.has(resource);
  return false; // unknown role
};

/** Check if a role can delete a given resource (Owner or Admin) */
export const canDelete = (role: Role, _resource?: Resource): boolean => {
  return role === ROLES.OWNER || role === ROLES.ADMIN;
};

/** Check if a role can access the Management section */
export const canAccessManagement = (role: Role): boolean => {
  return role === ROLES.OWNER || role === ROLES.ADMIN;
};

// ── Legacy / generic helpers (still used by navigation + layout) ───

/** Check if user has required role */
export const hasRequiredRole = (userRole: string, requiredRole?: string): boolean => {
  if (!requiredRole) return true;

  const roleHierarchy: Record<string, number> = {
    [ROLES.OWNER]: 4,
    [ROLES.ADMIN]: 3,
    [ROLES.STAFF]: 2,
  };

  return (roleHierarchy[userRole] || 0) >= (roleHierarchy[requiredRole] || 0);
};

/** Check if user has required permission level */
export const hasRequiredPermission = (userRole: string, requiredPermission?: string): boolean => {
  if (userRole === ROLES.OWNER) return true;
  if (userRole === ROLES.ADMIN) return true;
  if (userRole === ROLES.STAFF)
    return requiredPermission === PERMISSIONS.READ || requiredPermission === PERMISSIONS.WRITE;
  return false;
};

/** Check if user can access a navigation item */
export const canAccessNavItem = (user: AuthUser | null, navItem: NavItem): boolean => {
  if (!user || !user.role) return false;

  const hasRole = hasRequiredRole(user.role, navItem.requiredRole);
  const hasPerm = hasRequiredPermission(user.role, navItem.requiredPermission);
  const hasPlan = navItem.requiredPlan ? canAccessFeature(user.plan, navItem.requiredPlan) : true;

  return hasRole && hasPerm && hasPlan;
};

/** Check if user can access a navigation section (section-level requiredRole) */
export const canAccessSection = (user: AuthUser | null, section: NavSection): boolean => {
  if (!user || !user.role) return false;
  if (section.requiredRole) {
    return hasRequiredRole(user.role, section.requiredRole);
  }
  return true;
};

/** Check if user can access a route */
export const canAccessRoute = (user: AuthUser | null, path: string): boolean => {
  if (!user || !user.role) return false;

  const navItem = getNavigationItemByPath(path);
  if (!navItem) return true; // Not in nav = allow (detail pages, etc.)

  return canAccessNavItem(user, navItem);
};

// ── Convenience flags (used by usePermissions hook) ────────────────

export const hasWriteAccess = (userRole: string): boolean => {
  return userRole === ROLES.OWNER || userRole === ROLES.ADMIN || userRole === ROLES.STAFF;
};

export const hasFullAccess = (userRole: string): boolean => {
  return userRole === ROLES.OWNER || userRole === ROLES.ADMIN;
};

/** Only Owner/Admin may open a guest's detail/profile */
export const canViewGuestDetail = (userRole: string): boolean => {
  return hasFullAccess(userRole);
};

/**
 * Only Owner/Admin see the Reservations List view (table + filters); Staff get
 * the calendar only. Presentation-only: Staff still read GET /reservations.
 */
export const canViewReservationList = (userRole: string): boolean => {
  return hasFullAccess(userRole);
};

export const isReadOnly = (_userRole: string): boolean => {
  return false;
};
